import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { ArrowRightIcon } from '../components/icons';

/**
 * Tenant billing surface — current plan, this month's usage,
 * tier ladder.  Reads /v1/admin/billing for the plan + usage when
 * the backend surfaces them; falls back to the Free tier display
 * when the response is empty.
 */
export function Billing() {
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);

  const [usage, setUsage] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const u = await api('/v1/admin/usage').catch(() => null);
      setUsage(u);
    } catch (e) {
      setError(e.message);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Until /v1/admin/billing surfaces the real plan, hardcode Free.
  const currentTier = 'free';

  return (
    <section className="stack" style={{ marginTop: 4 }}>
      <div>
        <h2 style={{ margin: '0 0 4px' }}>Billing</h2>
        <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 13.5 }}>
          Your plan, this month's usage, and what you unlock by
          upgrading.
        </p>
      </div>

      <section className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11.5, textTransform: 'uppercase', color: 'var(--text-3)', letterSpacing: '0.06em' }}>Current plan</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>Free</div>
            <div style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 2 }}>1k SDK MAU · 5 tours/content/checklists per project · single project</div>
          </div>
          <button type="button" className="btn btn--primary" disabled>
            <ArrowRightIcon /> Upgrade plan
          </button>
        </div>
      </section>

      <section className="card">
        <h3 className="card__title">Usage this month</h3>
        {error && <div className="state state--error">{error}</div>}
        {!usage && (
          <p style={{ color: 'var(--text-3)', fontSize: 13.5 }}>
            We'll show your monthly counters here as soon as your
            first end-user fires a request. Until then there's
            nothing to draw.
          </p>
        )}
        {usage && <UsageGrid usage={usage} />}
      </section>

      <section className="card">
        <h3 className="card__title">Tier ladder</h3>
        <div className="tier-grid">
          <TierCard
            id="free"
            name="Free"
            price="$0"
            current={currentTier === 'free'}
            limits={['1,000 SDK MAU', '1 project', '5 tours / content / checklists', '7-day audit retention', 'Pinpoint inbox']}
            gated={['AI-powered help suggestions', 'Custom sender domain', 'Outbound webhooks', 'Multi-environment']}
          />
          <TierCard
            id="personal"
            name="Personal"
            price="$39/mo"
            current={currentTier === 'personal'}
            limits={['10,000 SDK MAU', '3 projects', 'Unlimited tours / content', '30-day audit retention', 'Custom sender domain']}
            gated={['Multi-environment', 'SSO', 'Custom data retention']}
          />
          <TierCard
            id="team"
            name="Team"
            price="$249/mo"
            current={currentTier === 'team'}
            limits={['100,000 SDK MAU', '10 projects', 'Outbound webhooks (1k events/day)', 'AI-powered help suggestions', '90-day audit retention']}
            gated={['SSO', 'Multi-environment (3 envs)']}
            recommended
          />
          <TierCard
            id="enterprise"
            name="Enterprise"
            price="From $1,500/mo"
            current={currentTier === 'enterprise'}
            limits={['Unlimited SDK MAU', 'Unlimited projects', 'SSO + audit-log export', 'Multi-environment (unlimited)', 'Custom retention', '99.9% SLA + dedicated CSM']}
            gated={[]}
          />
        </div>
      </section>

      <style>{BILLING_CSS}</style>
    </section>
  );
}

function UsageGrid({ usage }) {
  const items = [
    { label: 'SDK requests',     value: usage.sdk_requests ?? 0 },
    { label: 'Event writes',     value: usage.event_writes ?? 0 },
    { label: 'Lambda invocations', value: usage.lambda_invocations ?? 0 },
    { label: 'DDB read units',   value: usage.ddb_read_units ?? 0 },
    { label: 'DDB write units',  value: usage.ddb_write_units ?? 0 },
    { label: 'S3 storage (MB)',  value: usage.s3_mb ?? 0 },
    { label: 'Emails sent',      value: usage.resend_emails ?? 0 },
    { label: 'Bedrock tokens',   value: usage.bedrock_tokens ?? 0 },
  ];
  return (
    <div className="usage-grid">
      {items.map((it) => (
        <div key={it.label} className="usage-cell">
          <div className="usage-cell__value">{formatNumber(it.value)}</div>
          <div className="usage-cell__label">{it.label}</div>
        </div>
      ))}
    </div>
  );
}

function TierCard({ id, name, price, current, limits, gated, recommended }) {
  return (
    <div className={`tier-card${current ? ' tier-card--current' : ''}${recommended ? ' tier-card--recommended' : ''}`}>
      <div className="tier-card__head">
        <div>
          <div className="tier-card__name">{name}</div>
          <div className="tier-card__price">{price}</div>
        </div>
        {current && <span className="badge badge--editor">Current</span>}
        {recommended && !current && <span className="badge badge--admin">Most popular</span>}
      </div>
      <div className="tier-card__section">
        <div className="tier-card__heading">Included</div>
        <ul>
          {limits.map((l) => <li key={l}>{l}</li>)}
        </ul>
      </div>
      {gated.length > 0 && (
        <div className="tier-card__section">
          <div className="tier-card__heading tier-card__heading--muted">Not in this tier</div>
          <ul className="tier-card__muted-list">
            {gated.map((g) => <li key={g}>{g}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function formatNumber(n) {
  if (typeof n !== 'number') return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
}

const BILLING_CSS = `
  .tier-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; margin-top: 10px; }
  .tier-card { padding: 16px; border: 1px solid var(--border-1); border-radius: 12px; background: var(--surface-0); display: flex; flex-direction: column; gap: 12px; }
  .tier-card--current { border-color: var(--brand-600, #4f46e5); box-shadow: 0 0 0 1px var(--brand-600, #4f46e5); }
  .tier-card--recommended { background: linear-gradient(180deg, #f5f3ff, var(--surface-0)); }
  .tier-card__head { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; }
  .tier-card__name { font-size: 18px; font-weight: 700; }
  .tier-card__price { font-size: 13px; color: var(--text-3); margin-top: 2px; }
  .tier-card__heading { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-3); margin-bottom: 4px; }
  .tier-card__heading--muted { color: #94a3b8; }
  .tier-card ul { margin: 0; padding-left: 18px; font-size: 13px; line-height: 1.55; color: var(--text-2); }
  .tier-card__muted-list { color: #94a3b8; }
  .usage-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px; margin-top: 8px; }
  .usage-cell { padding: 10px 12px; background: var(--surface-1); border-radius: 10px; }
  .usage-cell__value { font-size: 18px; font-weight: 700; font-variant-numeric: tabular-nums; }
  .usage-cell__label { font-size: 12px; color: var(--text-3); margin-top: 2px; }
`;
